import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  const match = priceStr.replace(/[+$]/g, '').split('/')[0].trim();
  return parseFloat(match) || 0;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('bliss_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('bliss_cart', JSON.stringify(cart));
  }, [cart]);

  const _sameLine = (a, b) =>
    a.name === b.name &&
    a.category === b.category &&
    (a.size || '') === (b.size || '') &&
    (a.flavor || '') === (b.flavor || '');

  // opts: { size?: string, sizePriceStr?: string, flavor?: string }
  const addToCart = (item, category, opts = {}) => {
    const priceStr = opts.sizePriceStr || item.price;
    const line = {
      name: item.name,
      category,
      size: opts.size || '',
      flavor: opts.flavor || '',
      priceStr,
      price: parsePrice(priceStr),
      quantity: 1,
    };

    setCart((prev) => {
      const existing = prev.find((i) => _sameLine(i, line));
      if (existing) {
        return prev.map((i) =>
          _sameLine(i, line) ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, line];
    });
  };

  const removeLine = (line) => {
    setCart((prev) => prev.filter((i) => !_sameLine(i, line)));
  };

  const updateQuantity = (line, delta) => {
    setCart((prev) =>
      prev
        .map((i) =>
          _sameLine(i, line) ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const findLine = (name, category, size = '', flavor = '') =>
    cart.find((i) => _sameLine(i, { name, category, size, flavor }));

  const clearCart = () => setCart([]);

  const totalItems = cart.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeLine,
        updateQuantity,
        findLine,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
